import React from "react";
import { TrendingUp, TrendingDown, Check, Database, Camera, Settings2, MessageSquare } from "lucide-react";
import Reveal from "../common/Reveal";
import CountUp from "../common/CountUp";

const CATEGORIES = [
  {
    key: "data",
    label: "DATA INTEGRATION",
    title: "데이터 연동",
    icon: Database,
    cases: [
      {
        company: "정밀가공 제조기업 D사",
        status: "구축 완료",
        title: "CNC·MCT 설비데이터 연동으로 생산현황 실시간 관리",
        summary:
          "CNC·MCT 설비의 가동상태와 생산정보를 자동 수집하여 POP에서 설비별 현황을 통합 관리합니다.",
        features: ["다수 설비 통합 모니터링", "실시간 가동상태 확인", "설비별 생산이력 추적"],
      },
      {
        company: "수산식품 제조기업 E사",
        status: "구축 완료",
        title: "전자저울 인터페이스 연동으로 중량 데이터 자동 수집",
        summary:
          "현장 전자저울의 측정값을 POP에 실시간 연계하여 라인별 중량 현황과 검사이력을 체계적으로 관리합니다.",
        features: ["수기 입력 최소화", "중량 판정 표준화", "검사이력 추적관리"],
      },
      {
        company: "식품 제조기업 F사",
        status: "구축 완료",
        title: "성형설비 데이터 연동으로 생산조건과 실적 통합관리",
        summary:
          "설비별 사이클타임·생산량·온도 데이터를 자동 수집하여 POP에서 생산현황과 공정조건을 실시간 관리합니다.",
        features: ["공정조건 실시간 확인", "생산실적 자동 집계", "설비별 이력 추적"],
      },
    ],
  },
  {
    key: "vision",
    label: "AI VISION",
    title: "AI 비전 검사",
    icon: Camera,
    cases: [
      {
        company: "접착소재 제조기업 A사",
        status: "구축 완료",
        title: "AI 비전 검사 자동화로 품질과 생산성을 동시에 개선",
        summary:
          "AI Detector · AI Ruler와 MES를 연계하여 육안검사와 길이검사 중심의 품질검사를 자동화했습니다.",
        metrics: [
          { label: "시간당 생산량", before: "2,025 EA", after: "4,573 EA", change: "125.8% 증가", trend: "up" },
          { label: "월평균 품질 클레임", before: "2.92건", after: "0건", change: "100% 감소", trend: "down" },
          { label: "Detector 검사시간", before: "22.24초", after: "5.28초", change: "76.3% 단축", trend: "down" },
        ],
      },
      {
        company: "사출 제조기업 B사",
        status: "구축 진행",
        title: "벌크 제품 전수검사와 공정데이터 연계로 품질 추적체계 구축",
        summary:
          "AI 비전검사 2개 라인으로 촬영·판정·분류를 자동화하고, 검사결과를 MES·QMS·설비 데이터와 연계합니다.",
        metrics: [
          { label: "시간당 생산량", before: "2,546 EA/h", after: "2,864 EA/h", change: "12% 향상", trend: "up" },
          { label: "공정 불량률", before: "3.88%", after: "3.11%", change: "20% 감소", trend: "down" },
        ],
      },
      {
        company: "광학부품 제조기업 C사",
        status: "구축 진행",
        title: "AI 광패턴·휘도 검사로 렌즈 품질 판정 자동화",
        summary:
          "광패턴·휘도 검사와 AI 판정을 MT-ALL에 연계하여 수축 불량과 품질이력을 통합 관리합니다. AI 판정 정확도 98% 이상을 확보했습니다.",
        metrics: [
          { label: "시간당 생산량", before: "4,081 EA/h", after: "4,693 EA/h", change: "15% 향상", trend: "up" },
          { label: "공정 불량률", before: "10.73%", after: "9.44%", change: "12% 감소", trend: "down" },
        ],
      },
    ],
  },
  {
    key: "process",
    label: "PROCESS OPTIMIZATION",
    title: "AI 공정 최적화",
    icon: Settings2,
    cases: [
      {
        company: "자동차 커넥터 제조기업 H사",
        status: "구축 진행",
        title: "AI 분석 기반 사출조건 최적화로 품질과 생산성 동시 개선",
        summary:
          "사출설비 데이터와 레시피·스펙 정보를 분석하여 최적 조건을 제안하고 품질 이상과 설비 위험을 사전 관리합니다.",
        metrics: [
          { label: "양품 비율", before: "97.1%", after: "99.7%", change: "2.6%p 향상", trend: "up" },
          { label: "평균 사이클 타임", before: "29.2초", after: "27.8초", change: "1.4초 단축", trend: "down" },
        ],
      },
      {
        company: "전자부품 제조기업 G사",
        status: "구축 완료",
        title: "설비데이터와 AI 분석을 연계한 공정별 수율 관리",
        summary:
          "다공정 설비데이터를 수집하고 AI 분석 결과를 연계하여 공정 흐름과 수율지표를 POP에서 통합 관리합니다.",
        features: ["전체 공정 흐름 가시화", "AI 기반 수율 산출", "핵심 KPI 통합 관리"],
      },
    ],
  },
  {
    key: "llm",
    label: "AI LLM",
    title: "AI LLM",
    icon: MessageSquare,
    cases: [
      {
        company: "제조기업 J사",
        status: "구축 완료",
        title: "제조데이터 연계형 LLM 챗봇으로 현장정보 조회 자동화",
        summary:
          "생산·공정 데이터를 LLM 챗봇과 연계해 현장 담당자가 필요한 정보를 자연어로 신속하게 조회할 수 있도록 구축했습니다.",
        features: ["자연어 기반 정보조회", "현장정보 접근성 향상", "반복 조회업무 간소화"],
      },
    ],
  },
];

const StatusBadge = ({ status }) => (
  <span
    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
      status === "구축 완료"
        ? "bg-green-50 text-green-700"
        : "bg-accent-50 text-accent-700"
    }`}
  >
    {status}
  </span>
);

const CaseCard = ({ c }) => (
  <div className="relative h-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
    <div className="absolute inset-x-0 top-0 h-1.5 bg-primary-800" />
    <div className="p-6 pt-8">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-gray-500">{c.company}</p>
        <StatusBadge status={c.status} />
      </div>
      <h3 className="mt-1.5 break-keep text-lg font-bold leading-snug text-primary-900">
        {c.title}
      </h3>
      <p className="mt-3 break-keep text-sm leading-relaxed text-gray-600">
        {c.summary}
      </p>

      {c.metrics && (
        <div className="mt-5 space-y-3 border-t border-gray-100 pt-5">
          {c.metrics.map((m) => {
            const TrendIcon = m.trend === "up" ? TrendingUp : TrendingDown;
            return (
              <div key={m.label} className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-gray-500">{m.label}</div>
                  <div className="mt-0.5 text-base font-bold text-primary-900">
                    <CountUp text={m.before} />{" "}
                    <span className="font-normal text-gray-400">→</span>{" "}
                    <CountUp text={m.after} />
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-bold text-green-700">
                  <TrendIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <CountUp text={m.change} />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {c.features && (
        <ul className="mt-5 space-y-2 border-t border-gray-100 pt-5">
          {c.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" strokeWidth={2.5} />
              <span className="break-keep">{f}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const CaseStudies = () => {
  return (
    <section className="bg-gray-50 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {CATEGORIES.map((cat, idx) => (
          <div key={cat.key} className={idx > 0 ? "mt-16" : ""}>
            <Reveal className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-800">
                <cat.icon className="h-5 w-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-accent-600">
                  {cat.label}
                </span>
                <h2 className="text-xl font-bold text-primary-900">{cat.title}</h2>
              </div>
            </Reveal>

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {cat.cases.map((c, index) => (
                <Reveal key={c.company} delay={index * 80}>
                  <CaseCard c={c} />
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CaseStudies;
